/**
 * A sidebar built from the page's own headings.
 *
 * The content is editable, so a hand-written list of links would drift the
 * first time an officer adds a section. These are read out of the markdown
 * instead: add a heading, get a link.
 */

export interface TocEntry {
  id: string
  text: string
  level: number
}

/** The id a heading renders with, so the nav and the heading agree. */
export function headingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

/**
 * Pull `##` and `###` headings out of markdown.
 *
 * `#` is skipped: the page already has its own title, and a document that
 * starts at `##` keeps the nav one level deep and readable. Fenced code
 * blocks are ignored so a `#` comment inside one is not mistaken for a
 * heading.
 */
export function parseToc(markdown: string): TocEntry[] {
  const entries: TocEntry[] = []
  let inFence = false

  for (const line of markdown.split('\n')) {
    if (line.trimStart().startsWith('```')) {
      inFence = !inFence
      continue
    }
    if (inFence) continue

    const match = /^(#{2,3})\s+(.*)$/.exec(line.trim())
    if (!match) continue

    const text = match[2].replace(/[*_`]/g, '').trim()
    if (!text) continue
    entries.push({ id: headingId(text), text, level: match[1].length })
  }
  return entries
}

export function ContentToc({ entries }: { entries: TocEntry[] }) {
  if (entries.length < 2) return null

  return (
    <nav className="hidden lg:block lg:w-56 flex-shrink-0">
      <div className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto space-y-0.5 pr-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-2 mb-1">
          On this page
        </p>
        {entries.map(entry => (
          <a
            key={entry.id}
            href={`#${entry.id}`}
            className={`block py-1 rounded text-sm text-gray-400 hover:text-gray-100 hover:bg-raven-dark transition-colors ${
              entry.level === 3 ? 'pl-5 pr-2 text-xs' : 'px-2'
            }`}
          >
            {entry.text}
          </a>
        ))}
      </div>
    </nav>
  )
}
